// Brief 85 §2 — StageModal, moved out of App.tsx's renderModals switch.
//
// MOVED byte-identical then (sha in that PR); 109 §7c RECOMPOSED the body, so
// the byte-identity claim is history, not a property of this file: the layout
// is now row [Type | By] · Jurisdiction FULL width · row [Register | Start
// Date] · Notes, and By is the same autocomplete Combobox the jurisdiction
// field rides (one native select fewer, §6's report). §7d adds the second
// primary — "Save and create Event" — which runs the SAME save and opens the
// Agenda modal on the SAVED row (the handler lives in App, beside saveStage).
//
// Props are the UNION of the names the body actually reads, as before.

import Combobox from "../Combobox";
import FieldSelect from "../FieldSelect";
import { ModalLayout } from "../SharedUI";
import LinkChainFooter from "../LinkChainFooter";
import { resolveTaxonomyLabel } from "../../lib/taxonomyHelpers";
import type { TaxList, TFn } from "../../lib/taxonomyTypes";
import type { FieldSelectOption } from "../FieldSelect";
import type { makeModalFields } from "../modalFields";

type Vocab = ReturnType<typeof makeModalFields>;
type ModalForm = { _si?: string | null; by?: string | null; juId?: string | null; startDate?: string | null; type?: string | null };

type Props = {
  F: Vocab["F"];
  T: Vocab["T"];
  _mt: Vocab["_mt"];
  caseStageEntries: TaxList;
  cm: () => void;
  form: ModalForm;
  jurisdictions: readonly unknown[];
  partyRoleTax: { entries: NonNullable<TaxList> };
  /* 111 §5b — the chain replaces saveStage/saveStageAndEvent: chainStage(null)
     IS the plain save (saveStageCore + cm, link-aware when chained), and
     chainStage(kind) spawns the next modal prefilled. */
  chainStage: (next: "stage" | "task" | "event" | null) => boolean;
  linkState: {
    unlinkDisabled: boolean;
    spawnDisabled: boolean;
    onUnlinkAll: () => void;
    /* 112 §7 — the Links manager's door. */
    linkDisabled: boolean;
    onLinkManager: () => void;
  };
  stageTypesList: readonly FieldSelectOption[];
  t: TFn;
  updateField: (k: string, v: unknown) => void;
};

export default function StageModal({
  F,
  T,
  _mt,
  caseStageEntries,
  cm,
  form,
  jurisdictions,
  partyRoleTax,
  chainStage,
  linkState,
  stageTypesList,
  t,
  updateField,
}: Props) {
  return (
    <ModalLayout
      title={
        form._si
          ? _mt("Edit Stage")
          : t("modal.title.add_stage", { fallback: "Add Stage" })
      }
      onClose={cm}
      onSave={() => chainStage(null)}
      footer={
        /* 111 §5b — the shared chain footer (109 §7d's second primary
           subsumed: Save-and-create-Event is one of the three spawns now). */
        <LinkChainFooter
          kind="stage"
          onSave={() => chainStage(null)}
          onSpawn={(k) => chainStage(k)}
          spawnDisabled={linkState.spawnDisabled}
          unlinkDisabled={linkState.unlinkDisabled}
          onUnlinkAll={linkState.onUnlinkAll}
          linkDisabled={linkState.linkDisabled}
          onLinkManager={linkState.onLinkManager}
          onCancel={cm}
        />
      }
    >
        <>
          <div className="field-row">
            <FieldSelect
              label={t("field.type_required", { fallback: "Type *" })}
              value={form.type}
              onChange={(v) => updateField("type", v)}
              options={[
                ["", t("common.select_dots", { fallback: "Select..." })],
                ...stageTypesList,
              ]}
              entries={caseStageEntries}
              t={t}
            />
            <div className="field">
              <label>{t("stage.field.by", { fallback: "By" })}</label>
              {/* §7c — the same autocomplete primitive as the jurisdiction
                  field below, over the SAME full party_role list the native
                  select mapped. */}
              <Combobox
                value={form.by || ""}
                onChange={(v: unknown) => updateField("by", v || "")}
                options={partyRoleTax.entries.flatMap((pr) =>
                  pr && !Array.isArray(pr) && pr.code
                    ? [
                        {
                          value: pr.code,
                          label: resolveTaxonomyLabel(
                            pr.code,
                            partyRoleTax.entries,
                          ),
                        },
                      ]
                    : [],
                )}
                placeholder={t("common.none_dashed", { fallback: "— None —" })}
              />
            </div>
          </div>
          <div className="field">
            <label>
              {t("field.jurisdiction", { fallback: "Jurisdiction" })}
            </label>
            <Combobox
              value={form.juId ? String(form.juId) : ""}
              onChange={(v) => updateField("juId", v ? +v : "")}
              options={jurisdictions.map((j: any) => ({
                value: String(j.id),
                label: j.name || "(unnamed)",
                sub: j.city || "",
              }))}
              placeholder={t("common.none_dashed", {
                fallback: "— None —",
              })}
            />
          </div>
          <div className="field-row">
            {F("Register Number", "registerNumber", "A/00/00000")}
            <div className="field">
              <label>
                {/* 111 §4c — the `*` goes with the requirement: `field.
                    start_date` is already seeded en/fr/nl (censused); the
                    `_required` rows stay seeded and unread (D28). */}
                {t("field.start_date", {
                  fallback: "Start Date",
                })}
              </label>
              <input
                className="field-input"
                type="date"
                value={(form.startDate || "").slice(0, 10)}
                onChange={(e) => updateField("startDate", e.target.value)}
              />
            </div>
          </div>
          {T("Notes", "notes")}
        </>
    </ModalLayout>
  );
}
